// src/hooks/useConsentGate.js
//
// P0-5 (Profil Derin Analiz V2) — KVKK açık rıza kapısı.
//
// Auth hazır olunca BİR KEZ kontrol eder (useWelcomeWizard ile aynı desen):
//   - localStorage cache "done" ise Firestore'a hiç gitmez (idempotency).
//   - users/{uid}/consents/{CURRENT_CONSENT_VERSION} varsa cache'le, kapat.
//   - Yoksa ConsentSheet açılır; accept() callable'ı çağırır, başarıda
//     cache'ler ve kapatır.
//   - Kontrol BELİRLENEMEZSE (offline/permission → null) fail-open:
//     uygulama kilitlenmez, cache yazılmaz, sonraki oturumda tekrar denenir.
//
// Returns: { open, busy, error, accept }

import { useCallback, useEffect, useState } from "react";
import {
  CURRENT_CONSENT_VERSION,
  hasCurrentConsent,
  recordConsent,
} from "../services/consent";

const CACHE_PREFIX = `mlsa.consent.${CURRENT_CONSENT_VERSION}:`;

function readCache(uid: string) {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage?.getItem(CACHE_PREFIX + uid);
  } catch (_) {
    return null;
  }
}

function writeCache(uid: string) {
  try {
    if (typeof window !== "undefined") {
      window.localStorage?.setItem(CACHE_PREFIX + uid, "done");
    }
  } catch (_) {}
}

export default function useConsentGate(uid: string | null | undefined) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!uid) {
      setOpen(false);
      setError("");
      return;
    }
    if (readCache(uid) === "done") return;

    let cancelled = false;
    (async () => {
      const has = await hasCurrentConsent(uid);
      if (cancelled) return;
      if (has === true) {
        writeCache(uid);
        return;
      }
      // null (belirlenemedi) → fail-open: sheet açma, cache yazma.
      if (has === false) setOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const accept = useCallback(async () => {
    if (!uid || busy) return;
    setBusy(true);
    setError("");
    try {
      await recordConsent();
      writeCache(uid);
      setOpen(false);
    } catch (e) {
      setError("Onay kaydedilemedi. Lütfen tekrar dene.");
      // eslint-disable-next-line no-console
      // `catch` değişkeni `unknown` — okuma salt tip düzeyinde.
      const hata = e as { code?: unknown; message?: unknown } | undefined;
      console.warn("[consent] recordConsent failed:", hata?.code || hata?.message);
    } finally {
      setBusy(false);
    }
  }, [uid, busy]);

  return { open, busy, error, accept };
}
